/*
 * Molt — the one visual system on this site.
 *
 * Black smoke hangs over an image. Domain-warped noise rises through it; the
 * pointer pushes it aside (a decaying trail texture); scroll clears it. The same
 * smoke, at card scale, rolls across a product and leaves its front behind.
 *
 * Simplex noise: Ashima Arts / Ian McEwan, MIT License
 * (https://github.com/ashima/webgl-noise). Everything else is original.
 */
(() => {
  'use strict';

  const REDUCE = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  /* ---------------------------------------------------------------- GLSL */

  const NOISE = `
    vec3 mod289(vec3 x){return x-floor(x*(1./289.))*289.;}
    vec2 mod289(vec2 x){return x-floor(x*(1./289.))*289.;}
    vec3 permute(vec3 x){return mod289(((x*34.)+1.)*x);}
    float snoise(vec2 v){
      const vec4 C=vec4(.211324865405187,.366025403784439,-.577350269189626,.024390243902439);
      vec2 i=floor(v+dot(v,C.yy));vec2 x0=v-i+dot(i,C.xx);
      vec2 i1=(x0.x>x0.y)?vec2(1.,0.):vec2(0.,1.);
      vec4 x12=x0.xyxy+C.xxzz;x12.xy-=i1;i=mod289(i);
      vec3 p=permute(permute(i.y+vec3(0.,i1.y,1.))+i.x+vec3(0.,i1.x,1.));
      vec3 m=max(.5-vec3(dot(x0,x0),dot(x12.xy,x12.xy),dot(x12.zw,x12.zw)),0.);
      m=m*m;m=m*m;
      vec3 x=2.*fract(p*C.www)-1.;vec3 h=abs(x)-.5;vec3 ox=floor(x+.5);vec3 a0=x-ox;
      m*=1.79284291400159-.85373472095314*(a0*a0+h*h);
      vec3 g;g.x=a0.x*x0.x+h.x*x0.y;g.yz=a0.yz*x12.xz+h.yz*x12.yw;
      return 130.*dot(m,g);
    }
    float fbm(vec2 p){
      float f=0.,a=.5;
      mat2 r=mat2(.8,.6,-.6,.8);
      for(int i=0;i<OCTAVES;i++){f+=a*snoise(p);p=r*p*2.02;a*=.5;}
      return f;
    }
  `;

  const VERT = `
    attribute vec2 aPos;
    varying vec2 vUv;
    void main(){ vUv = aPos * .5 + .5; gl_Position = vec4(aPos, 0., 1.); }
  `;

  // Pointer trail: previous frame, gently diffused and decayed, plus a splat.
  const TRAIL_FRAG = `
    precision mediump float;
    varying vec2 vUv;
    uniform sampler2D uPrev;
    uniform vec2 uTexel;
    uniform vec2 uPoint;
    uniform vec2 uPrevPoint;
    uniform float uAspect;
    uniform float uRadius;
    uniform float uForce;
    float segDist(vec2 p, vec2 a, vec2 b){
      vec2 pa=p-a, ba=b-a;
      float h=clamp(dot(pa,ba)/max(dot(ba,ba),1e-6),0.,1.);
      return length(pa-ba*h);
    }
    void main(){
      float c = texture2D(uPrev, vUv).r * .4;
      c += texture2D(uPrev, vUv + vec2(uTexel.x, 0.)).r * .15;
      c += texture2D(uPrev, vUv - vec2(uTexel.x, 0.)).r * .15;
      c += texture2D(uPrev, vUv + vec2(0., uTexel.y)).r * .15;
      c += texture2D(uPrev, vUv - vec2(0., uTexel.y)).r * .15;
      c = max(c * .988 - .0028, 0.);
      vec2 p = vUv; p.x *= uAspect;
      vec2 a = uPrevPoint; a.x *= uAspect;
      vec2 b = uPoint; b.x *= uAspect;
      float d = segDist(p, a, b);
      c += uForce * exp(-(d*d) / (uRadius*uRadius));
      gl_FragColor = vec4(min(c, 1.), 0., 0., 1.);
    }
  `;

  const HERO_FRAG = `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uImg;
    uniform sampler2D uTrail;
    uniform vec2 uRes;
    uniform vec2 uImgRes;
    uniform vec2 uFocus;
    uniform vec2 uLight;
    uniform float uTime;
    uniform float uOpen;
    uniform float uShed;
    uniform float uReady;
    ${NOISE}

    vec2 cover(vec2 uv){
      float rs = uRes.x / uRes.y, ri = uImgRes.x / uImgRes.y;
      vec2 s = rs > ri ? vec2(1., ri / rs) : vec2(rs / ri, 1.);
      return (uv - .5) * s + mix(vec2(.5), uFocus, 1. - s);
    }

    // rising smoke: low-frequency billows, gently warped so they curl rather than vein
    float smoke(vec2 p, float t){
      vec2 q = vec2(fbm(p * .55 + vec2(0., -t * .045)), fbm(p * .55 + vec2(5.2, 1.3) - vec2(t * .015, t * .04)));
      float big = fbm(p * .6 + q * .9 + vec2(0., -t * .05));
      float mid = fbm(p * 1.4 + q * .6 + vec2(3.1, -t * .08));
      return big * .78 + mid * .22;
    }

    void main(){
      vec2 uv = vUv;
      float aspect = uRes.x / uRes.y;
      float t = uTime;

      float trail = texture2D(uTrail, uv).r;
      vec2 tg = vec2(texture2D(uTrail, uv + vec2(.006, 0.)).r - texture2D(uTrail, uv - vec2(.006, 0.)).r,
                     texture2D(uTrail, uv + vec2(0., .006)).r - texture2D(uTrail, uv - vec2(0., .006)).r);

      // the cursor pushes smoke aside: sample the field upstream of the trail
      vec2 p = vec2(uv.x * aspect, uv.y) * 1.3 + tg * 2.2;
      float d = smoke(p, t) * .5 + .5;

      // smoke pools at the bottom, hangs at the top and edges, thins over the shirts
      float frame = smoothstep(.36, .0, uv.y) * .65 + smoothstep(.72, 1., uv.y) * .32 + pow(abs(uv.x - .5) * 2., 2.4) * .42;
      float density = d + frame - .45 - trail * 1.15 - uOpen * .16 - uShed * 1.3;
      float cover_ = smoothstep(-.08, .5, density);
      cover_ = cover_ * cover_ * (3. - 2. * cover_);

      vec3 img = texture2D(uImg, cover(uv + tg * .005)).rgb;
      // smoke is black but not flat: the thick of each billow is a hair lighter
      vec3 haze = vec3(.014, .014, .013) + vec3(.05, .048, .045) * smoothstep(.45, .95, d);
      vec3 col = mix(img, haze, cover_ * .96);
      // a soft grey lip where thin smoke meets the light behind it
      float lip = smoothstep(.0, .2, density) * (1. - smoothstep(.2, .5, density));
      vec3 L = normalize(vec3(uLight - uv, 1.));
      col += vec3(.11, .108, .104) * lip * .35 * (.6 + .4 * L.z);

      gl_FragColor = vec4(col * uReady, 1.);
    }
  `;

  const CARD_FRAG = `
    precision highp float;
    varying vec2 vUv;
    uniform sampler2D uA;
    uniform sampler2D uB;
    uniform vec2 uRes;
    uniform vec2 uResA;
    uniform vec2 uResB;
    uniform vec2 uOrigin;
    uniform float uP;
    uniform float uSeed;
    ${NOISE}
    vec2 cover(vec2 uv, vec2 ir){
      float rs = uRes.x / uRes.y, ri = ir.x / ir.y;
      vec2 s = rs > ri ? vec2(1., ri / rs) : vec2(rs / ri, 1.);
      return (uv - .5) * s + .5;
    }
    void main(){
      vec2 uv = vUv;
      float aspect = uRes.x / uRes.y;
      vec2 p = vec2(uv.x * aspect, uv.y) * 2.2 + uSeed;
      // a puff of black smoke rolls through; the front is waiting behind it
      vec2 drift = vec2(0., -uP * .9);
      float nz = fbm(p + drift + fbm(p * .7 - drift) * .9) * .5 + .5;
      vec2 d = uv - uOrigin; d.x *= aspect;
      float radial = 1. - clamp(length(d) / 1.25, 0., 1.);
      float open = nz * .5 + radial * .5 + uP * 1.2 - .6;
      float reveal = smoothstep(.36, .64, open);
      float puff = sin(3.14159 * uP) * smoothstep(.15, .75, nz) * (1. - abs(open - .5) * 1.2);
      vec3 a = texture2D(uA, cover(uv, uResA)).rgb;
      vec3 b = texture2D(uB, cover(uv, uResB)).rgb;
      vec3 col = mix(a, b, reveal);
      col = mix(col, vec3(.02, .02, .019), clamp(puff, 0., 1.) * .88);
      gl_FragColor = vec4(col, 1.);
    }
  `;

  /* -------------------------------------------------------------- helpers */

  function compile(gl, type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
      console.warn('[molt]', gl.getShaderInfoLog(s));
      gl.deleteShader(s);
      return null;
    }
    return s;
  }

  function program(gl, frag, octaves) {
    const v = compile(gl, gl.VERTEX_SHADER, VERT);
    const f = compile(gl, gl.FRAGMENT_SHADER, `#define OCTAVES ${octaves}\n` + frag);
    if (!v || !f) return null;
    const p = gl.createProgram();
    gl.attachShader(p, v);
    gl.attachShader(p, f);
    gl.bindAttribLocation(p, 0, 'aPos');
    gl.linkProgram(p);
    if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
      console.warn('[molt]', gl.getProgramInfoLog(p));
      return null;
    }
    const uniforms = {};
    const count = gl.getProgramParameter(p, gl.ACTIVE_UNIFORMS);
    for (let i = 0; i < count; i++) {
      const info = gl.getActiveUniform(p, i);
      uniforms[info.name] = gl.getUniformLocation(p, info.name);
    }
    return { p, u: uniforms };
  }

  function quad(gl) {
    const b = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, b);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  }

  function texture(gl, source) {
    const t = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, t);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    if (source) {
      gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    }
    return t;
  }

  function target(gl, w, h) {
    const tex = texture(gl, null);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, w, h, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    const fb = gl.createFramebuffer();
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { tex, fb, w, h };
  }

  function loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'async';
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = src;
    });
  }

  const damp = (a, b, k, dt) => a + (b - a) * (1 - Math.exp(-k * dt));
  const clamp01 = (v) => Math.min(1, Math.max(0, v));
  const easeOutExpo = (x) => (x >= 1 ? 1 : 1 - Math.pow(2, -10 * x));
  const easeInOutCubic = (x) => (x < .5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);

  /* ----------------------------------------------------------------- hero */

  function hero(section) {
    const canvas = section.querySelector('[data-molt-canvas]');
    if (!canvas) return;
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    const src = (mobile && canvas.dataset.srcMobile) || canvas.dataset.src;
    if (!src) return;

    const gl = canvas.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false, powerPreference: 'high-performance' });
    if (!gl) return;

    const octaves = mobile ? 3 : 4;
    const draw = program(gl, HERO_FRAG, octaves);
    const trailProg = program(gl, TRAIL_FRAG, 1);
    if (!draw || !trailProg) return;
    quad(gl);

    const dprCap = mobile ? 1 : 1.25;
    let W = 0, H = 0, trailA, trailB, imgTex, imgRes = [1, 1];
    const focus = [parseFloat(canvas.dataset.focusX) || .5, 1 - (parseFloat(canvas.dataset.focusY) || .5)];

    const pointer = { x: .5, y: .5, px: .5, py: .5, force: 0, last: 0, active: false };
    const light = { x: .7, y: .8 };
    const state = { open: 0, shed: 0, shedTarget: 0, ready: 0, visible: true, start: 0 };

    function resize() {
      const r = section.getBoundingClientRect();
      const dpr = Math.min(window.devicePixelRatio || 1, dprCap);
      W = Math.max(2, Math.round(r.width * dpr));
      H = Math.max(2, Math.round(r.height * dpr));
      canvas.width = W;
      canvas.height = H;
      const tw = Math.max(64, Math.round(r.width / 4)), th = Math.max(64, Math.round(r.height / 4));
      if (!trailA || trailA.w !== tw || trailA.h !== th) {
        [trailA, trailB].forEach((t) => t && (gl.deleteTexture(t.tex), gl.deleteFramebuffer(t.fb)));
        trailA = target(gl, tw, th);
        trailB = target(gl, tw, th);
      }
    }

    function onMove(x, y) {
      const r = section.getBoundingClientRect();
      const nx = (x - r.left) / r.width, ny = 1 - (y - r.top) / r.height;
      if (nx < 0 || nx > 1 || ny < 0 || ny > 1) return;
      pointer.x = nx;
      pointer.y = ny;
      pointer.active = true;
      pointer.last = performance.now();
    }
    section.addEventListener('pointermove', (e) => onMove(e.clientX, e.clientY), { passive: true });
    section.addEventListener('touchmove', (e) => e.touches[0] && onMove(e.touches[0].clientX, e.touches[0].clientY), { passive: true });

    function step(now, dt) {
      const t = (now - state.start) / 1000;

      // When nobody is steering (phones, or a still mouse) a slow ghost drifts
      // through the frame so the skin never looks frozen.
      const idle = now - pointer.last > 2600;
      if (idle) {
        const gx = .62 + Math.sin(t * .23) * .26 + Math.sin(t * .51) * .07;
        const gy = .5 + Math.sin(t * .31 + 1.3) * .22;
        pointer.x = damp(pointer.x, gx, 1.6, dt);
        pointer.y = damp(pointer.y, gy, 1.6, dt);
      }
      const vx = pointer.x - pointer.px, vy = pointer.y - pointer.py;
      const speed = Math.hypot(vx, vy) / Math.max(dt, 1 / 240);
      const targetForce = Math.min(1, speed * (idle ? .5 : .9)) * (idle ? .16 : .34);
      pointer.force = damp(pointer.force, targetForce, 10, dt);

      light.x = damp(light.x, pointer.x, 2.2, dt);
      light.y = damp(light.y, pointer.y, 2.2, dt);

      state.shed = damp(state.shed, state.shedTarget, 6, dt);
      const intro = clamp01((now - state.start) / 2800);
      state.open = easeOutExpo(intro);
      state.ready = damp(state.ready, 1, 3.5, dt);

      // trail pass
      gl.useProgram(trailProg.p);
      gl.bindFramebuffer(gl.FRAMEBUFFER, trailB.fb);
      gl.viewport(0, 0, trailB.w, trailB.h);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, trailA.tex);
      gl.uniform1i(trailProg.u.uPrev, 0);
      gl.uniform2f(trailProg.u.uTexel, 1 / trailA.w, 1 / trailA.h);
      gl.uniform2f(trailProg.u.uPoint, pointer.x, pointer.y);
      gl.uniform2f(trailProg.u.uPrevPoint, pointer.px, pointer.py);
      gl.uniform1f(trailProg.u.uAspect, W / H);
      gl.uniform1f(trailProg.u.uRadius, mobile ? .16 : .11);
      gl.uniform1f(trailProg.u.uForce, pointer.force * state.open);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      [trailA, trailB] = [trailB, trailA];
      pointer.px = pointer.x;
      pointer.py = pointer.y;

      // composite
      gl.useProgram(draw.p);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.viewport(0, 0, W, H);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, imgTex);
      gl.uniform1i(draw.u.uImg, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, trailA.tex);
      gl.uniform1i(draw.u.uTrail, 1);
      gl.uniform2f(draw.u.uRes, W, H);
      gl.uniform2f(draw.u.uImgRes, imgRes[0], imgRes[1]);
      gl.uniform2f(draw.u.uFocus, focus[0], focus[1]);
      gl.uniform2f(draw.u.uLight, light.x, light.y);
      gl.uniform1f(draw.u.uTime, 40 + t);
      gl.uniform1f(draw.u.uOpen, state.open);
      gl.uniform1f(draw.u.uShed, state.shed);
      gl.uniform1f(draw.u.uReady, state.ready);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }

    let raf = 0, lastT = 0;
    function loop(now) {
      raf = 0;
      if (!state.visible || document.hidden) return;
      const dt = Math.min(.05, (now - lastT) / 1000 || 1 / 60);
      lastT = now;
      step(now, dt);
      raf = requestAnimationFrame(loop);
    }
    const kick = () => { if (!raf && state.visible && !document.hidden) { lastT = performance.now(); raf = requestAnimationFrame(loop); } };

    function onScroll() {
      const r = section.getBoundingClientRect();
      state.shedTarget = easeInOutCubic(clamp01(-r.top / (r.height * .85)));
    }

    loadImage(src).then((img) => {
      imgTex = texture(gl, img);
      imgRes = [img.naturalWidth, img.naturalHeight];
      resize();
      state.start = performance.now();
      section.classList.add('is-live');
      new IntersectionObserver(([en]) => { state.visible = en.isIntersecting; kick(); }).observe(section);
      document.addEventListener('visibilitychange', kick);
      window.addEventListener('scroll', onScroll, { passive: true });
      let rt;
      window.addEventListener('resize', () => { clearTimeout(rt); rt = setTimeout(resize, 120); });
      onScroll();
      kick();
    }).catch(() => { /* photo stays visible under its CSS scrim */ });

    canvas.addEventListener('webglcontextlost', (e) => {
      e.preventDefault();
      cancelAnimationFrame(raf);
      raf = 0;
      state.visible = false;
      section.classList.remove('is-live');
    });
  }

  /* --------------------------------------------------- card back → front */

  const Cards = {
    gl: null, canvas: null, prog: null, cache: new Map(), current: null, raf: 0, seed: 0,

    init() {
      if (!FINE || REDUCE) return;
      const canvas = document.createElement('canvas');
      canvas.setAttribute('aria-hidden', 'true');
      const gl = canvas.getContext('webgl', { antialias: false, alpha: false, premultipliedAlpha: false });
      if (!gl) return;
      this.prog = program(gl, CARD_FRAG, 3);
      if (!this.prog) return;
      quad(gl);
      this.gl = gl;
      this.canvas = canvas;
      canvas.addEventListener('webglcontextlost', (e) => {
        e.preventDefault();
        this.gl = null;
        this.detach(true);
        document.querySelectorAll('.is-molt').forEach((m) => m.classList.remove('is-molt'));
      });
      this.bind(document);
    },

    bind(root) {
      if (!this.gl) return;
      root.querySelectorAll('[data-molt-a]').forEach((media) => {
        if (media.__molt) return;
        media.__molt = true;
        media.classList.add('is-molt');
        media.addEventListener('pointerenter', (e) => this.enter(media, e));
        media.addEventListener('pointerleave', () => this.leave(media));
        const card = media.closest('[data-card]');
        if (card) {
          card.addEventListener('pointerenter', (e) => { if (!media.contains(e.target)) this.enter(media, e); });
          card.addEventListener('pointerleave', () => this.leave(media));
          card.addEventListener('focusin', () => this.enter(media, null));
          card.addEventListener('focusout', (e) => { if (!card.contains(e.relatedTarget)) this.leave(media); });
        }
      });
    },

    tex(src) {
      if (this.cache.has(src)) {
        const hit = this.cache.get(src);
        this.cache.delete(src);
        this.cache.set(src, hit);
        return hit.promise;
      }
      const entry = { promise: null, tex: null, res: [1, 1] };
      entry.promise = loadImage(src).then((img) => {
        if (!this.gl) throw new Error('lost');
        entry.tex = texture(this.gl, img);
        entry.res = [img.naturalWidth, img.naturalHeight];
        return entry;
      });
      this.cache.set(src, entry);
      while (this.cache.size > 24) {
        const [k, old] = this.cache.entries().next().value;
        if (old.tex && this.gl) this.gl.deleteTexture(old.tex);
        this.cache.delete(k);
      }
      return entry.promise;
    },

    enter(media, e) {
      if (!this.gl) return;
      if (this.current && this.current.media !== media) this.detach(true);
      const r = media.getBoundingClientRect();
      const origin = e ? [(e.clientX - r.left) / r.width, 1 - (e.clientY - r.top) / r.height] : [.5, .5];
      if (this.current && this.current.media === media) {
        this.current.target = 1;
        this.kick();
        return;
      }
      const cur = { media, p: 0, target: 1, origin, a: null, b: null, ready: false };
      this.current = cur;
      this.seed = Math.random() * 50;
      Promise.all([this.tex(media.dataset.moltA), this.tex(media.dataset.moltB)]).then(([a, b]) => {
        if (this.current !== cur) return;
        cur.a = a; cur.b = b; cur.ready = true;
        const dpr = Math.min(window.devicePixelRatio || 1, 2);
        this.canvas.width = Math.round(r.width * dpr);
        this.canvas.height = Math.round(r.height * dpr);
        media.appendChild(this.canvas);
        media.classList.add('is-gl');
        this.kick();
      }).catch(() => media.classList.remove('is-molt'));
    },

    leave(media) {
      if (this.current && this.current.media === media) {
        this.current.target = 0;
        this.kick();
      }
    },

    detach(hard) {
      const cur = this.current;
      if (!cur) return;
      if (this.canvas.parentNode) this.canvas.parentNode.removeChild(this.canvas);
      cur.media.classList.remove('is-gl');
      if (hard) this.current = null;
    },

    kick() {
      if (this.raf) return;
      let last = performance.now();
      const tick = (now) => {
        this.raf = 0;
        const cur = this.current;
        if (!cur || !cur.ready || !this.gl) return;
        const dt = Math.min(.05, (now - last) / 1000);
        last = now;
        // asymmetric: melting in is slow and viscous, closing is a touch quicker
        const k = cur.target > cur.p ? 2 : 3.4;
        cur.p = damp(cur.p, cur.target, k, dt);
        if (Math.abs(cur.p - cur.target) < .004) cur.p = cur.target;
        this.draw(cur);
        if (cur.p === 0 && cur.target === 0) {
          this.detach(true);
          return;
        }
        if (cur.p !== cur.target) this.raf = requestAnimationFrame(tick);
      };
      this.raf = requestAnimationFrame(tick);
    },

    draw(cur) {
      const gl = this.gl, u = this.prog.u;
      gl.viewport(0, 0, this.canvas.width, this.canvas.height);
      gl.useProgram(this.prog.p);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, cur.a.tex);
      gl.uniform1i(u.uA, 0);
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, cur.b.tex);
      gl.uniform1i(u.uB, 1);
      gl.uniform2f(u.uRes, this.canvas.width, this.canvas.height);
      gl.uniform2f(u.uResA, cur.a.res[0], cur.a.res[1]);
      gl.uniform2f(u.uResB, cur.b.res[0], cur.b.res[1]);
      gl.uniform2f(u.uOrigin, cur.origin[0], cur.origin[1]);
      gl.uniform1f(u.uP, cur.p);
      gl.uniform1f(u.uSeed, this.seed);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    }
  };

  /* ---------------------------------------------------------------- boot */

  function boot() {
    if (!REDUCE) document.querySelectorAll('[data-molt-hero]').forEach(hero);
    Cards.init();
  }

  window.Molt = { bind: (root) => Cards.bind(root || document) };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
